import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  ArrowLeft, Newspaper, Plus, Edit3, Trash2, Save, X, Loader2, Eye, EyeOff,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";

const blank = {
  name: "",
  short: "",
  url: "",
  color: "#6CABDD",
  filter: "",
  active: true,
};

export default function AdminNewsSources() {
  const { user } = useAuth();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/news-sources");
      setSources(r.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggleActive = async (s) => {
    try {
      await api.put(`/admin/news-sources/${s.id}`, { active: !s.active });
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? News from this source will stop appearing.`)) return;
    try {
      await api.delete(`/admin/news-sources/${s.id}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin-news-sources"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <RouterLink
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Admin
          </RouterLink>
          <span className="label-kicker text-[var(--brand-body)]">
            News sources · {user?.name || user?.email}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-12 sm:px-8">
        <div className="mb-8 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-[#6CABDD]">
            <Newspaper size={16} strokeWidth={2.5} />
            <span className="label-kicker">News feed editor</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Choose the voices on{" "}
            <RouterLink to="/news" className="underline decoration-[#6CABDD] underline-offset-4">
              /news
            </RouterLink>.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
            Add RSS feeds. Toggle any off to hide temporarily without losing the URL.
            Use the <span className="font-mono text-xs">filter</span> field to only keep
            headlines containing a word — handy for general sports feeds (e.g.{" "}
            <span className="font-mono text-xs">coventry</span>).
          </p>
        </div>

        <SourceForm onSaved={load} setError={setError} />

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-news-sources-error"
          >
            {error}
          </p>
        ) : null}

        <h2 className="mb-4 font-display text-lg font-bold">
          In rotation ({sources.length})
        </h2>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : sources.length === 0 ? (
          <div
            className="rounded-md border border-dashed border-black/15 bg-white p-8 text-center text-sm text-[var(--brand-body)]"
            data-testid="admin-news-sources-empty"
          >
            No sources yet. Add your first RSS above.
          </div>
        ) : (
          <ul className="overflow-hidden rounded-md border border-black/10 bg-white" data-testid="admin-news-sources-list">
            {sources.map((s) => (
              <li
                key={s.id}
                className="flex flex-col gap-3 border-b border-black/5 px-5 py-4 last:border-b-0 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  {editing?.id === s.id ? (
                    <SourceInlineEdit
                      src={s}
                      onCancel={() => setEditing(null)}
                      onSaved={async () => {
                        setEditing(null);
                        await load();
                      }}
                      setError={setError}
                    />
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: s.color }}
                        />
                        <p className="truncate font-display text-base font-bold">
                          {s.name}
                          <span className="ml-2 label-kicker text-[var(--brand-body)]">
                            · {s.short}
                          </span>
                          {!s.active ? (
                            <span className="ml-2 rounded-sm bg-[#475569]/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--brand-body)]">
                              off
                            </span>
                          ) : null}
                        </p>
                      </div>
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 block truncate text-xs text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
                      >
                        {s.url}
                      </a>
                      {s.filter ? (
                        <p className="mt-1 text-xs text-[var(--brand-body)]">
                          keeps headlines containing:{" "}
                          <span className="font-mono">{s.filter}</span>
                        </p>
                      ) : null}
                    </>
                  )}
                </div>
                {editing?.id !== s.id ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => toggleActive(s)}
                      data-testid={`admin-source-toggle-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
                    >
                      {s.active ? <EyeOff size={12} /> : <Eye size={12} />}
                      {s.active ? "Hide" : "Show"}
                    </button>
                    <button
                      onClick={() => setEditing(s)}
                      data-testid={`admin-source-edit-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17]"
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(s)}
                      data-testid={`admin-source-delete-${s.id}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-[#E63946] px-3 py-1.5 label-kicker text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}

function SourceForm({ onSaved, setError }) {
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => {
    const v = k === "active" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.post("/admin/news-sources", {
        name: form.name.trim(),
        short: form.short.trim(),
        url: form.url.trim(),
        color: form.color,
        filter: form.filter.trim() || null,
        active: form.active,
      });
      setForm(blank);
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="mb-10 rounded-md border border-black/10 bg-white p-6"
      data-testid="admin-news-source-form"
    >
      <h2 className="font-display text-lg font-bold">Add a news source</h2>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Labeled label="Name">
          <input
            required
            value={form.name}
            onChange={set("name")}
            data-testid="admin-source-name"
            placeholder="e.g. BBC Sport"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </Labeled>
        <Labeled label="Short label (for pills)">
          <input
            required
            value={form.short}
            onChange={set("short")}
            data-testid="admin-source-short"
            placeholder="BBC"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </Labeled>
      </div>
      <Labeled label="RSS feed URL" className="mt-4">
        <input
          required
          type="url"
          value={form.url}
          onChange={set("url")}
          data-testid="admin-source-url"
          placeholder="https://feeds.example.com/coventry/rss.xml"
          className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
        />
      </Labeled>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Labeled label="Brand colour">
          <input
            type="color"
            value={form.color}
            onChange={set("color")}
            data-testid="admin-source-color"
            className="h-10 w-full rounded-md border border-black/15 bg-white px-1 py-1 outline-none"
          />
        </Labeled>
        <Labeled label="Filter (optional)">
          <input
            value={form.filter}
            onChange={set("filter")}
            data-testid="admin-source-filter"
            placeholder="coventry"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]"
          />
        </Labeled>
        <label className="flex items-end gap-2 pb-1">
          <input
            type="checkbox"
            checked={form.active}
            onChange={set("active")}
            data-testid="admin-source-active"
            className="h-4 w-4 rounded border-black/20"
          />
          <span className="text-sm">Active immediately</span>
        </label>
      </div>
      <div className="mt-4 flex justify-end">
        <button
          type="submit"
          disabled={saving}
          data-testid="admin-source-save"
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2.5 font-display text-sm font-bold uppercase tracking-wide text-white disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          {saving ? "Adding…" : "Add source"}
        </button>
      </div>
    </form>
  );
}

function SourceInlineEdit({ src, onCancel, onSaved, setError }) {
  const [form, setForm] = useState({
    name: src.name,
    short: src.short,
    url: src.url,
    color: src.color || "#6CABDD",
    filter: src.filter || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      await api.put(`/admin/news-sources/${src.id}`, {
        name: form.name.trim(),
        short: form.short.trim(),
        url: form.url.trim(),
        color: form.color,
        filter: form.filter.trim() || null,
      });
      await onSaved();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <input value={form.name} onChange={set("name")} className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]" />
        <input value={form.short} onChange={set("short")} className="rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]" />
      </div>
      <input value={form.url} onChange={set("url")} className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]" />
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        <input type="color" value={form.color} onChange={set("color")} className="h-10 w-full rounded-md border border-black/15 bg-white px-1 py-1 outline-none" />
        <input value={form.filter} onChange={set("filter")} placeholder="filter keyword (optional)" className="sm:col-span-2 rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17]" />
      </div>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-3 py-1.5 label-kicker text-white"
        >
          <Save size={12} /> Save
        </button>
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker"
        >
          <X size={12} /> Cancel
        </button>
      </div>
    </div>
  );
}

function Labeled({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
